import * as THREE from "three";
import { generateFeltTexture } from "./textures";

/**
 * Dimensions du grand tapis de feutrine rouge (x = largeur, z = profondeur) :
 * volontairement demesurees par rapport a l'emprise du tapis de jeu (cf.
 * render/cards3d.ts) pour deborder de l'ecran dans TOUTE orientation
 * plausible — la camera est tres rapprochee et son champ de vision s'elargit
 * beaucoup sur les ecrans etroits (cf. `resize()` dans render/scene.ts, qui
 * privilegie ne jamais couper une colonne) ; seul le feutre doit remplir
 * l'ecran, jamais la salle derriere. Une simple nappe plutot qu'une table en
 * bois taillee : presentation volontairement epuree, sur demande explicite.
 */
export const FELT_WIDTH = 30;
export const FELT_DEPTH = 60;
/**
 * Decalage en z du tapis par rapport a l'origine du monde : le tapis de jeu
 * n'est pas centre sur z = 0 (la rangee du haut est proche de la camera, les
 * colonnes descendent vers le joueur) — on centre donc le feutre (et la
 * cible de la camera, cf. render/scene.ts) sur le centre reel du tapis de
 * jeu plutot que sur l'origine.
 */
export const TABLE_CENTER_Z = -0.2;
/**
 * Empan "utile" autour du jeu, pour le placement des elements de decor poses
 * sur le feutre (chandeliers, chope, cf. render/environment.ts) : bien plus
 * modeste que FELT_WIDTH/FELT_DEPTH, cale sur la zone effectivement cadree
 * par la camera — un chandelier positionne via FELT_DEPTH se retrouverait
 * hors champ.
 */
export const DECOR_WIDTH = 7.4;
export const DECOR_DEPTH = 6.3;
/** Hauteur du sol de la salle sous le niveau du feutre (feutre = y = 0). */
export const FLOOR_Y = -2.4;

/**
 * Le "plateau" est une simple nappe de feutrine rouge posee a y = 0 :
 * toutes les positions de cartes s'expriment simplement au-dessus de ce
 * plan. Texture tuilee (pas de vignette bake dans l'image, qui redessinerait
 * un quadrillage de zones sombres sur une aussi grande surface) : l'effet de
 * lumiere centree vient du point lumineux de la scene (render/scene.ts) et
 * d'une vignette d'ecran en CSS (cf. #vignette dans src/style.css), pas de
 * la texture elle-meme.
 */
export function buildTable(): THREE.Group {
  const group = new THREE.Group();

  const feltMap = generateFeltTexture("#5f1f22", false);
  feltMap.wrapS = feltMap.wrapT = THREE.RepeatWrapping;
  // Un carreau tous les ~2 unites : le grain reste net a la taille reelle
  // d'une carte, plutot que d'etirer une seule image sur toute la nappe.
  feltMap.repeat.set(FELT_WIDTH / 2, FELT_DEPTH / 2);

  const felt = new THREE.Mesh(
    new THREE.PlaneGeometry(FELT_WIDTH, FELT_DEPTH),
    new THREE.MeshStandardMaterial({ map: feltMap, roughness: 0.95, metalness: 0 }),
  );
  felt.rotation.x = -Math.PI / 2;
  felt.position.set(0, 0, TABLE_CENTER_Z);
  felt.receiveShadow = true;
  group.add(felt);

  return group;
}
